import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { updateProfile } from '../services/api';
import { useTheme } from '../context/ThemeContext';
import { Loader2, ArrowRight, UserIcon } from 'lucide-react';

const TOKEN_STORAGE_KEY = 'auth_token';

function getValidRedirect(redirect: string | null): string | null {
  if (!redirect || typeof redirect !== 'string') return null;
  const decoded = decodeURIComponent(redirect.trim());
  if (decoded.startsWith('/') && !decoded.startsWith('//')) return decoded;
  return null;
}

const CompleteProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isDark } = useTheme();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const firstNameRef = useRef<HTMLInputElement>(null);
  const redirectTo = getValidRedirect(searchParams.get('redirect')) || '/';

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (!token) {
      navigate('/login', { replace: true });
      return;
    }
    firstNameRef.current?.focus();
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedFirst = firstName.trim();
    const trimmedLast = lastName.trim();

    if (!trimmedFirst) {
      setError('Please enter your first name.');
      return;
    }
    if (!trimmedLast) {
      setError('Please enter your last name.');
      return;
    }

    setError('');
    setIsSubmitting(true);

    try {
      await updateProfile(trimmedFirst, trimmedLast);
      navigate(redirectTo, { replace: true });
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        'Something went wrong. Please try again.';
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const Logo = () => (
    <header className="w-full py-8 flex justify-center">
      <div className="h-6">
        <img
          src={isDark ? '/turing-logo-white.svg' : '/turing-logo.svg'}
          alt="Turing Logo"
          className="h-full object-contain"
        />
      </div>
    </header>
  );

  const FooterEl = () => (
    <footer className="py-6 flex flex-col items-center gap-2">
      <svg
        viewBox="0 0 76 48"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="h-5 w-auto text-zinc-300 dark:text-zinc-700"
      >
        <path
          d="M37.673 0c1.715 0 3.368 0.018 5.022 0.06 -0.006 0.004 -1.074 0.303 -1.445 0.377C25.207 0.104 11.212 2.563 11.212 5.389c0 2.945 14.922 5.333 33.329 5.333 13.714 0 25.492 -1.327 30.608 -3.22 -1.324 3.653 -17.596 6.541 -37.477 6.541 -3.606 0 -7.093 -0.095 -10.394 -0.272 8.132 3.627 14.933 8.388 14.933 13.801 0 0.04 0.04 8.662 0.04 19.477 -0.113 0 -0.28 -2.225 -0.28 -2.225C40.164 10.308 -2.646 16.47 0.129 6.699 1.2 2.928 17.763 0 37.673 0"
          fill="currentColor"
        />
      </svg>
      <p className="text-xs text-zinc-400 dark:text-zinc-600">
        &copy; {new Date().getFullYear()} Turing Enterprises Inc.
      </p>
    </footer>
  );

  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-background flex flex-col font-sans text-zinc-900 dark:text-foreground relative">
      <Logo />
      <main className="flex-1 flex items-center justify-center px-4 pb-12 pt-4">
        <div className="w-full max-w-sm">
          <div className="w-full bg-white dark:bg-card rounded-2xl shadow-md border border-zinc-200 dark:border-border overflow-hidden">
            <div className="p-8 space-y-5">
              <div className="text-center">
                <div className="w-14 h-14 bg-zinc-100 dark:bg-secondary rounded-full flex items-center justify-center mx-auto mb-4">
                  <UserIcon className="w-6 h-6 text-zinc-600 dark:text-zinc-400" />
                </div>
                <h1 className="text-2xl font-semibold text-zinc-900 dark:text-zinc-100 tracking-tight">
                  Complete your profile
                </h1>
                <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1.5">
                  Let us know your name to get started.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                <div>
                  <label
                    htmlFor="firstName"
                    className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1.5"
                  >
                    First name
                  </label>
                  <input
                    ref={firstNameRef}
                    id="firstName"
                    type="text"
                    value={firstName}
                    onChange={(e) => {
                      setFirstName(e.target.value);
                      if (error) setError('');
                    }}
                    placeholder="Jane"
                    autoComplete="given-name"
                    className="w-full px-3 py-2.5 bg-zinc-50 dark:bg-secondary border border-zinc-200 dark:border-border rounded-lg text-sm text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900/10 dark:focus:ring-zinc-100/10 focus:border-zinc-400 dark:focus:border-zinc-500 transition-colors"
                  />
                </div>

                <div>
                  <label
                    htmlFor="lastName"
                    className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1.5"
                  >
                    Last name
                  </label>
                  <input
                    id="lastName"
                    type="text"
                    value={lastName}
                    onChange={(e) => {
                      setLastName(e.target.value);
                      if (error) setError('');
                    }}
                    placeholder="Doe"
                    autoComplete="family-name"
                    className="w-full px-3 py-2.5 bg-zinc-50 dark:bg-secondary border border-zinc-200 dark:border-border rounded-lg text-sm text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900/10 dark:focus:ring-zinc-100/10 focus:border-zinc-400 dark:focus:border-zinc-500 transition-colors"
                  />
                </div>

                {error && (
                  <p role="alert" className="text-xs text-red-500 dark:text-red-400">
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="mt-2 w-full bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 font-medium py-3 px-4 rounded-xl hover:bg-zinc-800 dark:hover:bg-zinc-200 transition-colors focus:outline-none focus:ring-2 focus:ring-zinc-900 dark:focus:ring-zinc-100 focus:ring-offset-2 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      Continue
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </main>
      <FooterEl />
    </div>
  );
};

export default CompleteProfilePage;
